import type { ReactNode } from 'react';
import {
  LIMITS,
  VOICE_RECEIVE_MODES,
  useSettingsStore,
  type Settings,
} from '../game/settings.js';
import { TOUCH_MODE } from '../game/touch.js';
import { cn } from '../lib/cn.js';
import { Button } from './button.jsx';

const Section = ({ title, children }: { title: string; children: ReactNode }) => (
  <section className="border-t border-white/10 pt-4">
    <h3 className="text-[11px] uppercase tracking-[0.25em] text-ink-muted">{title}</h3>
    <div className="mt-3 space-y-4">{children}</div>
  </section>
);

const Slider = ({
  label,
  hint,
  value,
  display,
  min,
  max,
  step,
  onChange,
  onCommit,
}: {
  label: string;
  // `| undefined` is required by exactOptionalPropertyTypes: callers spread
  // these in, and a spread can legitimately carry an explicit undefined.
  hint?: string | undefined;
  value: number;
  display: string;
  min: number;
  max: number;
  step: number;
  onChange: (value: number) => void;
  onCommit?: (() => void) | undefined;
}) => (
  <label className="block">
    <div className="flex items-baseline justify-between gap-4">
      <span className="text-sm">{label}</span>
      <span className="tabular text-sm text-ink-muted">{display}</span>
    </div>
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(event) => onChange(Number(event.target.value))}
      onPointerUp={onCommit}
      onKeyUp={onCommit}
      className="mt-2 w-full accent-[var(--color-team-blue)]"
    />
    {hint && <p className="mt-1 text-xs text-ink-muted">{hint}</p>}
  </label>
);

const Toggle = ({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string;
  hint?: string | undefined;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) => (
  <label className="flex cursor-pointer items-start justify-between gap-4">
    <span>
      <span className="block text-sm">{label}</span>
      {hint && <span className="mt-0.5 block text-xs text-ink-muted">{hint}</span>}
    </span>
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={cn(
        'mt-0.5 h-6 w-11 shrink-0 rounded-full border transition-colors',
        checked ? 'border-team-blue bg-team-blue/70' : 'border-white/15 bg-white/10',
      )}
    >
      <span
        className={cn(
          'block size-4 rounded-full bg-white transition-transform',
          checked ? 'translate-x-6' : 'translate-x-1',
        )}
      />
    </button>
  </label>
);

/**
 * Settings live in their own store, separate from match state, because they
 * outlive any one session and are written by React rather than by the engine.
 * The engine subscribes once and pushes the values into the input manager, the
 * audio engine and the renderer — see `GameEngine.applySettings`.
 *
 * @param onPreviewAudio plays a blip so the volume slider can be heard while set.
 */
export const SettingsPanel = ({
  onClose,
  onPreviewAudio,
}: {
  onClose: () => void;
  onPreviewAudio?: (() => void) | undefined;
}) => {
  const settings = useSettingsStore();
  const set = <K extends keyof Settings>(key: K, value: Settings[K]): void =>
    settings.update({ [key]: value } as Partial<Settings>);

  return (
    <div
      className="fixed inset-0 z-30 grid place-items-center bg-surface-900/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="max-h-[88vh] w-[min(460px,92vw)] animate-rise overflow-y-auto rounded-xl border border-white/10 bg-surface-800/95 p-7"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-baseline justify-between gap-4">
          <h2 className="text-2xl font-extrabold tracking-tight">Settings</h2>
          <button
            onClick={settings.reset}
            className="text-xs uppercase tracking-[0.15em] text-ink-muted hover:text-ink"
          >
            Reset
          </button>
        </div>

        <div className="mt-6 space-y-5">
          <Section title="Audio">
            <Slider
              label="Master volume"
              value={settings.masterVolume}
              display={`${Math.round(settings.masterVolume * 100)}%`}
              {...LIMITS.masterVolume}
              onChange={(value) => set('masterVolume', value)}
              onCommit={onPreviewAudio}
            />
          </Section>

          <Section title="Voice chat">
            <div>
              <span className="text-sm">Hear voice from</span>
              <div className="mt-2 grid grid-cols-3 gap-2">
                {VOICE_RECEIVE_MODES.map((mode) => (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => set('voiceReceive', mode)}
                    className={cn(
                      'rounded-md border px-2 py-1.5 text-[11px] font-semibold uppercase tracking-wider transition-colors',
                      settings.voiceReceive === mode
                        ? 'border-team-blue bg-team-blue/25 text-white'
                        : 'border-white/15 bg-white/5 text-ink-muted hover:text-ink',
                    )}
                  >
                    {mode === 'everyone' ? 'Everyone' : mode === 'team' ? 'Team only' : 'Off'}
                  </button>
                ))}
              </div>
              <p className="mt-1.5 text-xs text-ink-muted">
                "Team only" applies in matches; the lobby has no sides. Your own mic is
                separate — it always starts muted ({TOUCH_MODE ? 'tap 🎤' : 'press V'} to
                talk).
              </p>
            </div>
          </Section>

          <Section title="Mouse">
            <Slider
              label="Sensitivity"
              hint="The usual scale — copy the number you use in other shooters."
              value={settings.sensitivity}
              display={settings.sensitivity.toFixed(1)}
              {...LIMITS.sensitivity}
              onChange={(value) => set('sensitivity', value)}
            />
            <Slider
              label="Aim sensitivity"
              hint="Multiplier applied while holding the right mouse button."
              value={settings.adsSensitivityScale}
              display={`${Math.round(settings.adsSensitivityScale * 100)}%`}
              {...LIMITS.adsSensitivityScale}
              onChange={(value) => set('adsSensitivityScale', value)}
            />
            <Toggle
              label="Invert vertical"
              checked={settings.invertY}
              onChange={(value) => set('invertY', value)}
            />
          </Section>

          {TOUCH_MODE && (
            <Section title="Touch">
              <Slider
                label="Look sensitivity"
                hint="How far the view turns per swipe on the right half."
                value={settings.touchSensitivity}
                display={settings.touchSensitivity.toFixed(1)}
                {...LIMITS.touchSensitivity}
                onChange={(value) => set('touchSensitivity', value)}
              />
              <Toggle
                label="Auto fire"
                hint="Shoot automatically when your crosshair is on an enemy."
                checked={settings.touchAutoFire}
                onChange={(value) => set('touchAutoFire', value)}
              />
            </Section>
          )}

          <Section title="Video">
            <Slider
              label="Field of view"
              hint="Wider sees more of the arena; narrower makes distant players bigger."
              value={settings.fieldOfView}
              display={`${settings.fieldOfView}°`}
              {...LIMITS.fieldOfView}
              onChange={(value) => set('fieldOfView', value)}
            />
          </Section>

          <Section title="Interface">
            <Toggle
              label="Show ping and framerate"
              checked={settings.showStats}
              onChange={(value) => set('showStats', value)}
            />
          </Section>
        </div>

        <Button size="lg" className="mt-7" onClick={onClose}>
          Done
        </Button>
      </div>
    </div>
  );
};
